import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getLangInfo, withLangPrefix } from "@/i18n";

interface ServicePageLayoutProps {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  ctaText?: string;
  ctaLink?: string;
  showBottomCta?: boolean;
  showHero?: boolean;
  showContentImage?: boolean;
}

interface PageImageConfig {
  heroUrl: string;
  heroAltKey: string;
  heroCaptionKey: string;
  contentUrl: string;
  contentAltKey: string;
}

const withBaseUrl = (path: string) => {
  const base = import.meta.env.BASE_URL || "/";
  const normalizedBase = base.endsWith("/") ? base : `${base}/`;
  return `${normalizedBase}${path.replace(/^\//, "")}`;
};

const pageImageByPath: Record<string, PageImageConfig> = {
  "/courses": {
    heroUrl: "/images/services/courses-hero.avif",
    heroAltKey: "serviceLayout.images.courses.heroAlt",
    heroCaptionKey: "serviceLayout.images.courses.heroCaption",
    contentUrl: "/images/services/courses-content.avif",
    contentAltKey: "serviceLayout.images.courses.contentAlt",
  },
  "/operations": {
    heroUrl: "/images/services/operations-hero.avif",
    heroAltKey: "serviceLayout.images.operations.heroAlt",
    heroCaptionKey: "serviceLayout.images.operations.heroCaption",
    contentUrl: "/images/services/operations-content.avif",
    contentAltKey: "serviceLayout.images.operations.contentAlt",
  },
  "/ecommerce": {
    heroUrl: "/images/services/ecommerce-hero.avif",
    heroAltKey: "serviceLayout.images.ecommerce.heroAlt",
    heroCaptionKey: "serviceLayout.images.ecommerce.heroCaption",
    contentUrl: "/images/services/ecommerce-content.avif",
    contentAltKey: "serviceLayout.images.ecommerce.contentAlt",
  },
  "/interview": {
    heroUrl: "/images/services/interview-hero.avif",
    heroAltKey: "serviceLayout.images.interview.heroAlt",
    heroCaptionKey: "serviceLayout.images.interview.heroCaption",
    contentUrl: "/images/services/interview-content.avif",
    contentAltKey: "serviceLayout.images.interview.contentAlt",
  },
  "/activity": {
    heroUrl: "/images/services/activity-hero.avif",
    heroAltKey: "serviceLayout.images.activity.heroAlt",
    heroCaptionKey: "serviceLayout.images.activity.heroCaption",
    contentUrl: "/images/services/activity-content.avif",
    contentAltKey: "serviceLayout.images.activity.contentAlt",
  },
  "/certification": {
    heroUrl: "/images/services/certification-hero.avif",
    heroAltKey: "serviceLayout.images.certification.heroAlt",
    heroCaptionKey: "serviceLayout.images.certification.heroCaption",
    contentUrl: "/images/services/certification-content.avif",
    contentAltKey: "serviceLayout.images.certification.contentAlt",
  },
  "/web-dev": {
    heroUrl: "/images/services/web-dev-hero.avif",
    heroAltKey: "serviceLayout.images.webDev.heroAlt",
    heroCaptionKey: "serviceLayout.images.webDev.heroCaption",
    contentUrl: "/images/services/web-dev-content.avif",
    contentAltKey: "serviceLayout.images.webDev.contentAlt",
  },
  "/consulting": {
    heroUrl: "/images/services/consulting-hero.avif",
    heroAltKey: "serviceLayout.images.consulting.heroAlt",
    heroCaptionKey: "serviceLayout.images.consulting.heroCaption",
    contentUrl: "/images/services/consulting-content.avif",
    contentAltKey: "serviceLayout.images.consulting.contentAlt",
  },
  "/matrix": {
    heroUrl: "/images/services/matrix-hero.avif",
    heroAltKey: "serviceLayout.images.matrix.heroAlt",
    heroCaptionKey: "serviceLayout.images.matrix.heroCaption",
    contentUrl: "/images/services/matrix-content.avif",
    contentAltKey: "serviceLayout.images.matrix.contentAlt",
  },
  "/ip-incubation": {
    heroUrl: "/images/services/ip-incubation-hero.avif",
    heroAltKey: "serviceLayout.images.ipIncubation.heroAlt",
    heroCaptionKey: "serviceLayout.images.ipIncubation.heroCaption",
    contentUrl: "/images/services/ip-incubation-content.avif",
    contentAltKey: "serviceLayout.images.ipIncubation.contentAlt",
  },
  "/team-expression": {
    heroUrl: "/images/services/team-expression-hero.avif",
    heroAltKey: "serviceLayout.images.teamExpression.heroAlt",
    heroCaptionKey: "serviceLayout.images.teamExpression.heroCaption",
    contentUrl: "/images/services/team-expression-content.avif",
    contentAltKey: "serviceLayout.images.teamExpression.contentAlt",
  },
  "/contact": {
    heroUrl: "/images/services/contact-hero.avif",
    heroAltKey: "serviceLayout.images.contact.heroAlt",
    heroCaptionKey: "serviceLayout.images.contact.heroCaption",
    contentUrl: "/images/services/contact-content.avif",
    contentAltKey: "serviceLayout.images.contact.contentAlt",
  },
};

const defaultImageConfig: PageImageConfig = {
  heroUrl: "/images/services/default-hero.avif",
  heroAltKey: "serviceLayout.images.default.heroAlt",
  heroCaptionKey: "serviceLayout.images.default.heroCaption",
  contentUrl: "/images/services/default-content.avif",
  contentAltKey: "serviceLayout.images.default.contentAlt",
};

const ServicePageLayout = ({
  title,
  subtitle,
  children,
  ctaText,
  ctaLink = "/contact",
  showBottomCta = true,
  showHero = true,
  showContentImage = true,
}: ServicePageLayoutProps) => {
  const { pathname } = useLocation();
  const { lang, normalizedPath } = getLangInfo(pathname);
  const { t } = useTranslation();
  const imageConfig = pageImageByPath[normalizedPath] ?? defaultImageConfig;
  const ctaHref = withLangPrefix(ctaLink, lang);

  return (
    <div className="ttk-service-shell" data-service-page={normalizedPath}>
      {showHero ? (
        <section className="py-14 md:py-20 ttk-service-hero">
          <div className="absolute top-10 right-[12%] w-80 h-80 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-10 left-[8%] w-64 h-64 rounded-full bg-primary/5 blur-3xl pointer-events-none" />

          <div className="container mx-auto px-4 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">
              <div className="lg:col-span-7 rounded-3xl ttk-service-panel p-7 md:p-11 flex flex-col justify-center reveal-up">
                <p className="text-[11px] md:text-xs ttk-service-kicker mb-4">{t("layout.nav.systems")}</p>
                <h1 className="ttk-service-title font-semibold leading-tight mb-5">{title}</h1>
                <p className="ttk-service-subtitle max-w-2xl leading-8">{subtitle}</p>
              </div>

              <figure className="lg:col-span-5 reveal-up reveal-delay-1">
                <div className="overflow-hidden rounded-3xl ttk-service-image">
                  <img
                    src={withBaseUrl(imageConfig.heroUrl)}
                    alt={t(imageConfig.heroAltKey)}
                    width={1280}
                    height={840}
                    loading="eager"
                    decoding="async"
                    className="w-full h-[300px] sm:h-[360px] object-cover rounded-3xl ttk-service-breathe"
                  />
                </div>
                <figcaption className="mt-3 text-sm text-muted-foreground/80">{t(imageConfig.heroCaptionKey)}</figcaption>
              </figure>
            </div>
          </div>
        </section>
      ) : null}

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          {showContentImage ? (
            <figure className="max-w-4xl mx-auto mb-10 reveal-up">
              <div className="overflow-hidden rounded-3xl ttk-service-image">
                <img
                  src={withBaseUrl(imageConfig.contentUrl)}
                  alt={t(imageConfig.contentAltKey)}
                  width={1280}
                  height={840}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-[220px] sm:h-[300px] object-cover rounded-3xl ttk-service-breathe"
                />
              </div>
            </figure>
          ) : null}
          {children}
        </div>
      </section>

      {showBottomCta ? (
        <section className="py-16">
          <div className="container mx-auto px-4 text-center">
            <div className="reveal-up">
              <h2 className="text-2xl md:text-3xl font-semibold mb-6 tracking-tight">{t("serviceLayout.ctaTitle")}</h2>
              <Link
                to={ctaHref}
                className="inline-flex ttk-service-cta px-7 py-2.5 rounded-full font-semibold text-base md:px-9 md:py-3.5 md:text-lg hover:opacity-95 transition-opacity"
              >
                {ctaText ?? t("serviceLayout.defaultCta")}
              </Link>
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default ServicePageLayout;
